import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import useRequest from "../../api/useRequest";
import GlobalSettings from "./GlobalSettings";
import { optimizationCostPath, costConfirmationError } from "./optimizationCost";
import { useEffect, useState, useCallback, useRef } from "react";
import { LoadingBlur } from "../../components/LoadingBlur";
import {
  OptimizationOverlay,
  OPTIMIZATION_BUILD_MS,
} from "../../components/OptimizationOverlay";
import { BasegridOverlay } from "../../components/BasegridOverlay";
import { SwmmExportOverlay } from "../../components/SwmmExportOverlay";
import { DBRequest } from "../../api/dbRequest";
import MapGridDesigner from "../../components/map-grid-designer/MapGridDesigner";
import { Box, Button, Dialog, useTheme, DialogTitle, DialogContent, DialogContentText, DialogActions } from "@mui/material";
import * as Yup from "yup";
import ExoAlert from "../../components/exo/ExoAlert";
import { validateDataCollection } from "../../components/exo/checkValidation";
import { validateDataForOptimization } from "../../components/map-grid-designer/helperFunctions/validateDataForOptimization";
import { tokens } from "../../theme/theme";
import {
  isAsyncBasegridResponse,
  isRunningBasegridError,
} from "./basegridResponse";
import { findOverDepthNodeIds } from "../../components/map-grid-designer/helperFunctions/resultMetrics";

// The optimization endpoint now returns { elements: [...], messages: [...] }
// instead of a bare elements array. These helpers normalize both shapes so the
// rest of the app keeps working with a plain elements array, and expose any
// warning messages the optimizer attached to the result.
function extractResultElements(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.elements)) return data.elements;
  return [];
}
function extractResultMessages(data) {
  if (data && Array.isArray(data.messages)) return data.messages;
  return [];
}

const Scenario = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();


  const globalSettingsValidation = Yup.object().shape({
    units: Yup.string().required(t("Is required")),
    auto_divide_large_pipes: Yup.boolean(),
    max_pipe_length: Yup.number().when("auto_divide_large_pipes", {
      is: true,
      then: (schema) => schema.required("Is required"),
      otherwise: (schema) => schema.optional(),
    }),
    water_consumption: Yup.number().required(t("Is required")),
    peak_factor: Yup.number().required(t("Is required")),
    wastewater_return_factor: Yup.number().required(t("Is required")),
    infiltration: Yup.number().required(t("Is required")),
    first_year_population: Yup.number().required(t("Is required")),
    final_year_population: Yup.number().required(t("Is required")),
    max_outfall: Yup.number().required(t("Is required")),
    minimum_velocity: Yup.number().required(t("Is required")),
    maximum_velocity: Yup.number().required(t("Is required")),
    minimum_slope: Yup.number().required(t("Is required")),
    maximum_slope: Yup.number().required(t("Is required")),
    use_minimum_shear_stress: Yup.boolean(),
    minimum_shear_stress: Yup.number().when("use_minimum_shear_stress", {
      is: true,
      then: (schema) => schema.required("Is required"),
      otherwise: (schema) => schema.nullable().optional(),
    }),
    manning_roughness: Yup.number().required(t("Is required")),
    maximal_water_depth: Yup.number().required(t("Is required")),
    minimum_cover: Yup.number().required(t("Is required")),
    maximal_buried_depth: Yup.number().required(t("Is required")),
    available_sewer_diameters: Yup.array(),
    use_lift_stations_where_excavation_depth: Yup.boolean().required(
      t("Is required")
    ),
    max_number_of_pump_stations: Yup.number().required(t("Is required")),
  });

  const { apiData, setApiData, isLoading, setIsLoading } = useRequest({
    path: `scenarios/${id}`,
  });

  // get project
  const [project, setProject] = useState(null);

// settings
  const [openSettings, setOpenSettings] = useState(null);

  const onValidation = useCallback((isValid) => {
    // open settings if there is something missing
    setOpenSettings(!isValid);
  }, []); // Leeres Array, da setOpenSettings sich nie ändert

  const checkSettings = useCallback((settings) => {
    validateDataCollection(
      globalSettingsValidation,
      settings,
      onValidation,
      true
    );
  }, [globalSettingsValidation, onValidation]); // Abhängigkeiten von checkSettings

useEffect(() => {
  if (apiData?.project_id) {
    DBRequest({
      path: `projects/${apiData.project_id}`,
      onResponse: setProject,
    });
  }
}, [apiData?.project_id]);

useEffect(() => {
  if (apiData?.globalSettings && openSettings === null) {
    checkSettings(apiData.globalSettings);
  }
}, [apiData?.globalSettings, openSettings, checkSettings]);


  function handleUpdatedScenario(data) {
    // update the scenario data but keep the settings dialog open;
    // closing is now an explicit action (the X in the top right)
    setApiData(data);
  }


  // designer
  const tools = [
    "clear",
    "delete",
    "custom",
    "center",
    "node",
    "edge",
    "simplify",
  ];
  const [error, setError] = useState(null);
  // ids/names of nodes that are not connected to the main network (set by the
  // integrity check) so they can be highlighted in red on the map
  const [disconnectedNodeIds, setDisconnectedNodeIds] = useState([]);
  // ids of result nodes whose buried depth exceeds maximal_buried_depth (set
  // after an optimization) so they can be highlighted in red on the map
  const [overDepthNodeIds, setOverDepthNodeIds] = useState([]);
  // clear the disconnected-node highlighting as soon as the user edits the map
  const handleMapEdit = useCallback(() => {
    setDisconnectedNodeIds((prev) => (prev.length ? [] : prev));
  }, []);
  function handleSaveMapElements(mapElements) {
    DBRequest({
      path: `scenarios/${id}`,
      method: "patch",
      data: { mapElements: mapElements },
      onResponse: handleSavedElements,
      onLoading: setIsLoading,
      onError: handleError,
    });
  }
  function handleSavedElements(data) {
    setApiData({ ...apiData, mapElements: data.mapElements });
  }
  function handleError(errors) {
    setError({
      type: "error",
      message: errors?.response?.data?.message || errors?.message || t("An error occurred"),
    });
  }

  const customTools = [
    {
      onClick: () => setOpenSettings(!openSettings),
      label: t("Settings"),
      src: "/assets/svg/tools/settings.svg",
    },
  ];

  // actions
  const [optiData, setOptiData] = useState(null);
  const [confirmationType, setConfirmationType] = useState(null); // null | 'fast' | 'deep' | 'normal' | 'basegrid'
  const [deepOptCostData, setDeepOptCostData] = useState(null);
  const [dataForOptimization, setDataForOptimization] = useState(null);
  // pending basegrid request, held until the cost is confirmed
  const [dataForBasegrid, setDataForBasegrid] = useState(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  // dedicated loading overlays for the longer-running actions
  const [isGeneratingBasegrid, setIsGeneratingBasegrid] = useState(false);
  const [isExportingSwmm, setIsExportingSwmm] = useState(false);
  // SWMM download: dataset choice dialog (basegrid vs. optimization result)
  const [swmmDialogOpen, setSwmmDialogOpen] = useState(false);
  const [swmmBasegridData, setSwmmBasegridData] = useState(null);

  // Optimization result, held separately from the editable basegrid
  // (apiData.mapElements): basegrid edits never touch the result; only a new
  // optimization replaces it. resultVersion increments on each fresh result so
  // the designer can switch to the result view exactly then (not on load).
  const [optimizationResult, setOptimizationResult] = useState(null);
  const [resultVersion, setResultVersion] = useState(0);
  const resultInitializedRef = useRef(false);

  // the optimizer output carries no isSaved flag, so nodes would render in the
  // gray "not saved" color; the result is a frozen, read-only snapshot -> saved
  function markResultSaved(elements) {
    return (elements || []).map((el) => ({ ...el, isSaved: true }));
  }

  // initial result on load: the backend persists it per scenario
  // (optimizationResult, own MongoDB document). Fallback for legacy scenarios
  // that stored the optimization output as their mapElements (calc values set).
  useEffect(() => {
    // apiData.id marks a loaded scenario (mapElements may be null when the
    // basegrid is empty, but a persisted result can still exist)
    if (resultInitializedRef.current || !apiData?.id) return;
    resultInitializedRef.current = true;
    const maxDepth = apiData?.globalSettings?.maximal_buried_depth;
    if (apiData.optimizationResult) {
      const resultElements = markResultSaved(
        extractResultElements(apiData.optimizationResult)
      );
      setOptimizationResult(resultElements);
      // re-derive the over-depth highlighting for a persisted result (silent:
      // the warning dialog only fires when an optimization is actually run)
      setOverDepthNodeIds(findOverDepthNodeIds(resultElements, maxDepth));
      return;
    }
    const hasResults = (apiData.mapElements || []).some(
      (el) => el.type === "edge" && el.calc?.some((c) => c.value != null)
    );
    if (hasResults) {
      setOptimizationResult(apiData.mapElements);
      setOverDepthNodeIds(findOverDepthNodeIds(apiData.mapElements, maxDepth));
    }
  }, [apiData?.id, apiData?.mapElements, apiData?.optimizationResult]);

  function handleOptimizationResult(data) {
    const elements = extractResultElements(data);
    const messages = extractResultMessages(data);
    setOptimizationResult(markResultSaved(elements));
    setResultVersion((v) => v + 1);

    // post-optimization check: the optimization method can produce nodes that
    // exceed the configured maximum burial depth -> highlight them red and warn
    const overDepthIds = findOverDepthNodeIds(
      elements,
      apiData?.globalSettings?.maximal_buried_depth
    );
    setOverDepthNodeIds(overDepthIds);

    // surface the optimizer's own warning and/or the burial-depth warning
    const warnings = [];
    if (messages.length > 0) warnings.push(messages[0]);
    if (overDepthIds.length > 0) {
      warnings.push(
        `${t(
          "Some nodes exceed the maximum burial depth and are marked red."
        )} (${overDepthIds.length})`
      );
    }
    if (warnings.length > 0) {
      setError({
        type: "warning",
        message: warnings.join(" "),
        cancleLabel: t("Ok"),
      });
    }
    // persist so the result survives reloads; the basegrid stays untouched
    DBRequest({
      path: `scenarios/${id}`,
      method: "patch",
      data: { optimizationResult: elements },
      onError: handleError,
    });
  }

  const actions = [
    {
      label: t("Generate Basegrid"),
      color: "warning",
      variant: "outlined",
      onClick: generateBaseGrid,
    },
    {
      label: t("Calculate Dry Weather Inflow"),
      color: "warning",
      variant: "outlined",
      onClick: handleCalcDryWeatherInflow,
    },
    {
      label: t("Download SWMM File"),
      color: "success",
      variant: "outlined",
      onClick: handleDonwloadSWMM,
    },
    {
      label: t("Optimization"),
      color: "success",
      variant: "contained",
      onClick: checkForOptimization,
    },
  ];

  function generateBaseGrid(mapElements, projectAreas) {
    // a basegrid can only be generated on an empty map; otherwise the new grid
    // would be appended to the existing elements -> require a clear first
    const existing = mapElements || apiData.mapElements || [];
    if (existing.length > 0) {
      setError({
        type: "warning",
        message: t(
          "The map already contains elements. Please clear it first before generating a basegrid."
        ),
      });
      return;
    }

    const reqData = {
      projectAreas,
      settings: { ...apiData.globalSettings },
    };

    // fetch the estimated cost first (total pipe length the basegrid will
    // produce) and let the user confirm before the grid is generated
    setDataForBasegrid(reqData);
    DBRequest({
      path: `calculate/basegrid_cost`,
      method: "post",
      data: reqData,
      onResponse: (costData) => handleCostResponse(costData, "basegrid"),
      onLoading: setIsLoading,
      onError: handleError,
    });
  }

  function handleConfirmBasegrid() {
    const reqData = dataForBasegrid;
    setConfirmationType(null);
    setDeepOptCostData(null);
    setDataForBasegrid(null);
    if (!reqData) return;
    DBRequest({
      path: `calculate/basegrid/${id}`,
      method: "post",
      data: reqData,
      onResponse: handleBasegridResponse,
      onLoading: setIsGeneratingBasegrid,
      onError: handleBasegridError,
    });
  }

  function handleCancelBasegrid() {
    setConfirmationType(null);
    setDeepOptCostData(null);
    setDataForBasegrid(null);
  }

  function handleBasegridResponse(data) {
    if (isAsyncBasegridResponse(data)) {
      setError({
        type: "info",
        message: t(
          "The selected area is too large for direct calculation. It will be processed in the background and you will receive an email when it is finished."
        ),
        cancleLabel: t("Ok"),
      });
      return;
    }

    handleNewBaseGrid(data);
  }

  function handleBasegridError(error) {
    if (isRunningBasegridError(error)) {
      setError({
        type: "info",
        message: t(
          "A basegrid calculation is already running for this scenario. You will receive an email when it is finished."
        ),
        cancleLabel: t("Ok"),
      });
      return;
    }

    const message =
      error?.response?.data?.message ||
      error?.message ||
      t("Basegrid calculation failed");
    setError({ type: "error", message });
  }

  function handleNewBaseGrid(baseGridMapElements) {
    var newMapElements = baseGridMapElements;

    // add to existing
    if (apiData.mapElements)
      newMapElements = [...apiData.mapElements, ...baseGridMapElements];

    setApiData({
      ...apiData,
      mapElements: newMapElements,
    });
  }

  function checkForOptimization(mapElements) {
    const isValid = validateDataForOptimization(apiData.mapElements || []);
    if (isValid) {
      const reqData = {
        mapElements: mapElements,
        settings: { ...apiData.globalSettings },
      };
      DBRequest({
        path: `integrity_check`,
        method: "post",
        data: reqData,
        onResponse: (data) => handleItegrityCheck(data, reqData),
        onLoading: setIsLoading,
        onError: handleError,
      });
    } else {
      setError({
        type: "warning",
        message: t("You need to define at least one Outfall Node"),
      });
    }
  }

  function handleItegrityCheck(data, reqData) {
    if (data.components) {
      // The network is split into several components. The component(s) that
      // contain an outfall node form the "main" network; every node in a
      // component WITHOUT an outfall is not connected to a drain and gets
      // highlighted in red on the map.
      const components = data.components;

      // collect the identifiers (name and id) of all outfall nodes
      const outletKeys = new Set();
      (reqData.mapElements || []).forEach((el) => {
        if (el.type !== "node") return;
        const isOutlet = (el.props || []).some(
          (prop) => prop.key === "outlet_candidate" && prop.value
        );
        if (isOutlet) {
          outletKeys.add(String(el.name));
          outletKeys.add(String(el.id));
        }
      });

      const disconnected = [];
      components.forEach((comp) => {
        const hasOutlet = comp.nodes.some((node) =>
          outletKeys.has(String(node))
        );
        if (!hasOutlet) comp.nodes.forEach((node) => disconnected.push(node));
      });

      // safety net: if no outfall could be matched, fall back to keeping the
      // largest component so we never mark the whole network red
      if (outletKeys.size === 0) {
        disconnected.length = 0;
        let mainComponent = null;
        components.forEach((comp) => {
          if (!mainComponent || comp.nodes.length > mainComponent.nodes.length)
            mainComponent = comp;
        });
        components.forEach((comp) => {
          if (comp === mainComponent) return;
          comp.nodes.forEach((node) => disconnected.push(node));
        });
      }

      setDisconnectedNodeIds(disconnected);
      setError({
        type: "warning",
        message: t(
          "The network is not fully connected. Please check your connections."
        ),
      });
    } else {
      setDisconnectedNodeIds([]);
      setOptiData(reqData);
    }
  }

  function startOptimization(data) {
    setOptiData(null);
    setIsOptimizing(true);
    const startedAt = Date.now();

    DBRequest({
      path: `calculate/optimization/${id}`,
      method: "post",
      data: data,
      onResponse: (responseData) => {
        // let the build animation play through at least one full cycle before
        // the result replaces the overlay, even if the request returns quickly
        const remaining = Math.max(
          OPTIMIZATION_BUILD_MS - (Date.now() - startedAt),
          0
        );
        setTimeout(() => {
          // goes into the separate result slot — the basegrid stays untouched
          handleOptimizationResult(responseData);
          setIsOptimizing(false);
        }, remaining);
      },
      onError: (errors) => {
        setIsOptimizing(false);
        handleError(errors);
      },
    });
  }

  // Shared cost-confirmation handler for all cost-gated actions (deep/fast/
  // normal optimization and basegrid). Blocks when the balance is insufficient,
  // otherwise opens the matching confirmation dialog (type).
  function handleCostResponse(costData, type) {
    const costError = costConfirmationError(costData);
    if (costError) {
      setError({
        type: "error",
        message: t(costError),
      });
      setDataForOptimization(null);
      setDataForBasegrid(null);
    } else {
      setDeepOptCostData(costData);
      setConfirmationType(type);
    }
  }

  // Normal optimization: fetch the cost first, then show the confirmation
  // dialog; the preview includes this scenario's previously paid types.
  function handleOptimizationCostAndConfirm() {
    const reqData = optiData;
    if (!reqData) return;
    setDataForOptimization(reqData);
    setOptiData(null); // close the optimization selection dialog
    DBRequest({
      path: optimizationCostPath(id, "normal"),
      method: "post",
      data: reqData,
      onResponse: (costData) => handleCostResponse(costData, "normal"),
      onLoading: setIsLoading,
      onError: handleError,
    });
  }

  function handleConfirmOptimization() {
    const data = dataForOptimization;
    setConfirmationType(null);
    setDeepOptCostData(null);
    setDataForOptimization(null);
    startOptimization(data);
  }

  function handleCancelOptimization() {
    setConfirmationType(null);
    setDeepOptCostData(null);
    setDataForOptimization(null);
  }

function handleDeepOptimizationClick() {
    setDataForOptimization(optiData); // Store data for confirmation step
    DBRequest({
      path: optimizationCostPath(id, "deep"),
      method: "post",
      data: optiData,
      onResponse: (costData) => handleCostResponse(costData, "deep"), // <-- THIS IS THE FIX
      onLoading: setIsLoading,
      onError: handleError,
    });
    setOptiData(null); // Close the optimization selection dialog
  }

  function handleConfirmDeepOptimization() {
    startDeepOptimization(dataForOptimization);
    setConfirmationType(null);
    setDeepOptCostData(null);
    setDataForOptimization(null);
  }

  function handleCancelDeepOptimization() {
    setConfirmationType(null);
    setDeepOptCostData(null);
    setDataForOptimization(null);
  }

  function startDeepOptimization(data) {
    setOptiData(null);

    DBRequest({
      path: `calculate/deep_optimization/${id}`,
      method: "post",
      data: data,
      onResponse: () => {
        alert(`Deep Optimization started. Expect the results in < 24 hours. We will notify you as soon as the results are available via mail at: \n ${apiData.user.email}`);
        if (apiData?.project_id) navigate(`/projects/${apiData.project_id}`);
      },
      onLoading: setIsLoading,
      onError: handleError,
    });
  }

function handleFastOptimizationClick() {
    setDataForOptimization(optiData); // Store data for confirmation step
    DBRequest({
      path: optimizationCostPath(id, "fast"),
      method: "post",
      data: optiData,
      onResponse: (costData) => handleCostResponse(costData, "fast"), // Pass 'fast'
      onLoading: setIsLoading,
      onError: handleError,
    });
    setOptiData(null); // Close the optimization selection dialog
  }

  function handleConfirmFastOptimization() {
    startFastOptimization(dataForOptimization);
    setConfirmationType(null);
    setDeepOptCostData(null);
    setDataForOptimization(null);
  }

  function handleCancelFastOptimization() {
    setConfirmationType(null);
    setDeepOptCostData(null);
    setDataForOptimization(null);
  }

  function startFastOptimization(data) {
    setOptiData(null);

    DBRequest({
      path: `calculate/real_fast_optimization/${id}`,
      method: "post",
      data: data,
      onResponse: () => {
        alert(`Fast Optimization started. Expect the results in < 2 hours. We will notify you as soon as the results are available via mail at: \n ${apiData.user.email}`);
        if (apiData?.project_id) navigate(`/projects/${apiData.project_id}`);
      },
      onLoading: setIsLoading,
      onError: handleError,
    });
  }


  // Simplify (tool button in the basegrid view): sends the current network and
  // the rectangle the user drew (selection, a [lat,lng] polygon ring) to the
  // backend, which computes ALL three simplification variants in one call.
  // onResults receives { "1": elements, "2": elements, "3": elements } so the
  // designer can show a preview per degree; null signals a failed request.
  // No global loading overlay here — the preview dialog shows its own spinner.
  function handleSimplify(mapElements, selection, onResults) {
    DBRequest({
      path: `calculate/simplify`,
      method: "post",
      data: { mapElements, selection, degrees: [1, 2, 3] },
      onResponse: (data) =>
        onResults(data && data.results ? data.results : null),
      onError: (errors) => {
        handleError(errors);
        onResults(null);
      },
    });
  }

  function handleNewMapElements(data) {
    setApiData({
      ...apiData,
      mapElements: data,
    });
  }

  function handleCalcDryWeatherInflow(data) {
    const reqData = {
      mapElements: data,
      settings: { ...apiData.globalSettings },
    };
    DBRequest({
      path: `calculate/dwf`,
      method: "post",
      data: reqData,
      onResponse: handleNewMapElements,
      onLoading: setIsLoading,
      onError: handleError,
    });
  }

  // The SWMM download action hands us the editable basegrid. Instead of
  // downloading it right away we let the user choose between the basegrid and
  // the optimization result via a dialog (runSwmmDownload).
  function handleDonwloadSWMM(data) {
    setSwmmBasegridData(data);
    setSwmmDialogOpen(true);
  }

  function runSwmmDownload(isBasegrid) {
    const dataset = isBasegrid ? swmmBasegridData : optimizationResult;
    setSwmmDialogOpen(false);
    if (!dataset || dataset.length === 0) {
      setError({
        type: "error",
        message: t("No data available for the SWMM download"),
      });
      return;
    }
    DBRequest({
      path: `download/swmm`,
      method: "post",
      data: { mapElements: dataset, isBasegrid },
      onLoading: setIsExportingSwmm,
      onResponse: (res) => {
        if (res && res.url) {
          // trigger a real file download via an anchor click instead of
          // window.open (which pop-up blockers stop and which renders the .inp
          // inline in the browser). The S3 object is served with
          // Content-Disposition: attachment, so the browser downloads it.
          const link = document.createElement("a");
          link.href = res.url;
          link.download = `swmm_${isBasegrid ? "basegrid" : "result"}.inp`;
          document.body.appendChild(link);
          link.click();
          link.remove();
        } else
          setError({
            type: "error",
            message: t("SWMM download failed"),
          });
      },
      onError: (error) => {
        // surface the backend's message ({message}) rather than the generic
        // axios "Request failed with status code ..." string
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          t("SWMM download failed");
        setError({ type: "error", message });
      },
    });
  }

 return (
    <Box>
      <LoadingBlur active={!project || isLoading} />
      <OptimizationOverlay active={isOptimizing} />
      <BasegridOverlay active={isGeneratingBasegrid} />
      <SwmmExportOverlay active={isExportingSwmm} />
      {apiData && (
        <GlobalSettings
          onUpdate={handleUpdatedScenario}
          scenario={apiData}
          open={openSettings}
          startData={apiData.globalSettings}
          onClose={() => setOpenSettings(false)}
        />
      )}
      <MapGridDesigner
        actions={actions}
        startProjectAreas={project && project.projectAreas}
        startElements={apiData && apiData.mapElements}
        disconnectedNodeIds={disconnectedNodeIds}
        overDepthNodeIds={overDepthNodeIds}
        onEdit={handleMapEdit}
        tools={tools}
        onSave={handleSaveMapElements}
        onSimplify={handleSimplify}
        resultElements={optimizationResult}
        resultVersion={resultVersion}
        setOpenSettings={setOpenSettings}
        customTools={customTools}
        takeMeBack={{
          label: t("Back"),
          to: `/projects/${project && project.id}`,
        }}
        forceSaveOnActions
      />
      <ExoAlert
        open={error}
        alert={error || {}}
        onClose={() => setError(null)}
      />
      <Dialog
        open={Boolean(optiData)}
        sx={{
          bgcolor: "transparent",
          backdropFilter: "blur(5px)",
          ".MuiPaper-root": {
            bgcolor: "transparent",
            boxShadow: "none",
            width: "100%",
          },
        }}
        onClose={() => setOptiData(null)}
      >
        <Box className="flex items-center justify-evenly gap-4 w-full py-6">
          <OptiCard
            label={t("Optimization")}
            src={"/assets/svg/deep-opti.svg"}
            onClick={handleOptimizationCostAndConfirm}
          />
          <OptiCard
            label={t("Deep Optimization")}
            src={"/assets/svg/deep-optimization.svg"}
            disabled
          />
        </Box>
      </Dialog>
          <Dialog
      open={confirmationType === 'deep'}
        onClose={handleCancelDeepOptimization}
        fullWidth={true}
        maxWidth="md"
        >
        <DialogTitle>{t("Start Deep Optimization?")}</DialogTitle>
        <DialogContent>
        <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
          {deepOptCostData &&
            t(
              `Are you sure you want to start a deep optimization?\n\nCredits available: ${deepOptCostData.credit_balance}\nCharge: ${deepOptCostData.costs}\nNew Credit-Balance after: ${deepOptCostData.credits_left_after_purchase}`
            )}
        </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleConfirmDeepOptimization} color="primary" variant="contained" autoFocus>
            {t("Confirm")}
            </Button>
            <Button onClick={handleCancelDeepOptimization} color="primary" variant="contained">
            {t("Cancel")}
            </Button>
        </DialogActions>
        </Dialog>
       <Dialog
        open={confirmationType === 'fast'}
        onClose={handleCancelFastOptimization}
        fullWidth={true}
        maxWidth="md"
        >
        <DialogTitle>{t("Start Fast Optimization?")}</DialogTitle>
        <DialogContent>
        <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
          {deepOptCostData &&
            t(
              `Are you sure you want to start a fast optimization?\n\nCredits available: ${deepOptCostData.credit_balance}\nCharge: ${deepOptCostData.costs}\nNew Credit-Balance after: ${deepOptCostData.credits_left_after_purchase}`
            )}
        </DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={handleConfirmFastOptimization} color="primary" variant="contained" autoFocus>
            {t("Confirm")}
            </Button>
            <Button onClick={handleCancelFastOptimization} color="primary" variant="contained">
            {t("Cancel")}
            </Button>
        </DialogActions>
        </Dialog>
      <Dialog
        open={confirmationType === 'normal'}
        onClose={handleCancelOptimization}
        fullWidth={true}
        maxWidth="md"
      >
        <DialogTitle>{t("Start Optimization?")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
            {deepOptCostData &&
              `${t("Are you sure you want to start the optimization?")}\n\n${t(
                "Credits available"
              )}: ${deepOptCostData.credit_balance}\n${t("Charge")}: ${
                deepOptCostData.costs
              }\n${t("New Credit-Balance after")}: ${
                deepOptCostData.credits_left_after_purchase
              }`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmOptimization} color="primary" variant="contained" autoFocus>
            {t("Confirm")}
          </Button>
          <Button onClick={handleCancelOptimization} color="primary" variant="contained">
            {t("Cancel")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={confirmationType === 'basegrid'}
        onClose={handleCancelBasegrid}
        fullWidth={true}
        maxWidth="md"
      >
        <DialogTitle>{t("Generate Basegrid?")}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ whiteSpace: 'pre-line' }}>
            {deepOptCostData &&
              `${t("Are you sure you want to generate the basegrid?")}\n\n${t(
                "Credits available"
              )}: ${deepOptCostData.credit_balance}\n${t("Estimated charge")}: ${
                deepOptCostData.costs
              }\n${t("New Credit-Balance after")}: ${
                deepOptCostData.credits_left_after_purchase
              }`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConfirmBasegrid} color="primary" variant="contained" autoFocus>
            {t("Confirm")}
          </Button>
          <Button onClick={handleCancelBasegrid} color="primary" variant="contained">
            {t("Cancel")}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={swmmDialogOpen}
        onClose={() => setSwmmDialogOpen(false)}
        fullWidth={true}
        maxWidth="sm"
      >
        <DialogTitle>{t("Download SWMM File")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Which dataset would you like to export?")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => runSwmmDownload(true)}
            color="primary"
            variant="contained"
          >
            {t("Basegrid")}
          </Button>
          <Button
            onClick={() => runSwmmDownload(false)}
            color="primary"
            variant="contained"
            disabled={!optimizationResult}
          >
            {t("Result")}
          </Button>
          <Button onClick={() => setSwmmDialogOpen(false)} color="primary">
            {t("Cancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const OptiCard = ({ label, onClick, src, disabled = false }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      variant="contained"
      className={`flex flex-col items-center w-[180px] gap-4 ${
        disabled ? "" : "hover:scale-105 hover:transition-all"
      }`}
      sx={{
        borderRadius: "15px",
        bgcolor: colors.contrast[200],
        padding: "18px 10px",
        opacity: disabled || !onClick ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : undefined,
        "&.Mui-disabled": {
          bgcolor: colors.contrast[200],
          color: "inherit",
        },
        ":hover": {
          bgcolor: colors.contrast[200],
        },
      }}
    >
    <img src={src} alt={label} className="w-[120px] h-[120px] object-contain"/>
      {label}
    </Button>
  );
};

export default Scenario;
